// TRINITI Organics - Promotion IQ Data Layer
// "Was this the best investment we could make with this retailer — and how do we do better next time?"

export interface Retailer {
    retailer_id: string;
    name: string;
    economic_model: "Pricing-Led" | "Hybrid";
    promo_cadence: string;
    lock_in_window_days: number;
    pricing_lock_in_days: number;
    tool_lock_in_days: number;
    execution_reliability: number;
    elasticity_sensitivity: "Low" | "Medium" | "High";
    roi_index: number;
    incrementality_ratio: number;
    margin_volatility: number;
    margin_structure: number; // percentage
    reconciliation_likelihood: number; // likelihood of post-event refunds
    ytd_sales: number;
    ytd_units: number;
    avg_merchandising_pct: number;
  }
  
  export interface SKU {
    sku_id: string;
    brand: string;
    category: string;
    base_price: number;
    variety: string;
    unit_measure: string;
  }
  
  export interface HistoricalPromotion {
    promotion_id: string;
    retailer_id: string;
    sku_id: string;
    discount_depth_pct: number;
    discount_depth_cents: number;
    tools: string[];
    trade_spend: number;
    incremental_units: number;
    incremental_revenue: number;
    net_margin: number;
    volume_split: {
      incremental_consumption: number;
      pantry_loading: number;
      forward_buying: number;
      subsidized_base: number;
    };
    timing_effects: {
      pre_promo_lift: number;
      in_promo_lift: number;
      post_promo_dip: number;
    };
    status: "Completed";
    period: string;
    planned_margin?: number;
    actual_vs_planned?: number;
  }
  
  export interface PlannedPromotion {
    promotion_id: string;
    retailer_id: string;
    sku_id: string;
    planned_discount_pct: number;
    planned_discount_cents: number;
    tools: string[];
    planned_trade_spend: number;
    expected_incremental_revenue: number;
    expected_margin: number;
    risk_level: "Low" | "Medium" | "High";
    confidence: number;
    status: "Planned" | "Frozen";
    promo_window: string;
    auto_tag: "Scale" | "Fix" | "Kill" | "Review";
    ownership: {
      sales: string;
      rgm: string;
      finance: string;
    };
  }
  
  export interface ToolCategory {
    category: string;
    tools: PromotionTool[];
  }
  
  export interface PromotionTool {
    id: string;
    name: string;
    category: string;
    cost_multiplier: number;
    lift_contribution: number;
    elasticity_interaction: number;
    historical_effectiveness: number;
  }
  
  export interface ScenarioRules {
    elasticity_rules: Record<"Low" | "Medium" | "High", number>;
    tool_lift: Record<string, number>;
    margin_decay_threshold_pct: number;
    margin_penalty_per_extra_pct: number;
  }
  
  export interface GeoFencingMetrics {
    promotion_id: string;
    geo_fencing: {
      impressions: number;
      store_visits: number;
      store_visit_conversion: number;
      purchases: number;
      purchase_conversion: number;
      incremental_units: number;
      cost_per_incremental_unit: number;
    };
  }
  
  export interface BudgetGuardrails {
    total_trade_budget: number;
    committed_spend: number;
    flexible_spend: number;
    allocations: {
      retailer_id: string;
      allocated_pct: number;
      fair_share_pct: number;
    }[];
    flexibility_pct: number;
    regulatory_safe_window_days: number;
  }
  
  export interface DecisionLock {
    promotion_id: string;
    pricing_lock_date: string;
    tool_lock_date: string;
    contracted_components: string[];
    flexible_components: string[];
    locked: boolean;
    locked_by?: string;
    locked_date?: string;
  }
  
  export interface Learning {
    promotion_id: string;
    planned_margin: number;
    actual_margin: number;
    planned_incrementality: number;
    actual_incrementality: number;
    root_cause: "Elasticity Miss" | "Execution Failure" | "Timing Distortion" | "Retailer Behavior";
    root_cause_detail: string;
    learning: string;
    confidence_update: number;
    assumptions_validated: string[];
    assumptions_broken: string[];
  }
  
  export interface BaselineDiagnostics {
    retailer_id: string;
    sku_id: string;
    baseline_curve: number[];
    seasonality_overlay: number[];
    promo_weeks: number[];
    non_promo_weeks: number[];
    confidence_score: number;
    syndicated_estimate: number;
  }
  
  export interface MonitoringAlert {
    promotion_id: string;
    alert_type: "Lift Variance" | "Execution Issue" | "Margin At Risk";
    severity: "Low" | "Medium" | "High";
    message: string;
    expected_value: number;
    actual_value: number;
    variance_pct: number;
  }
  
  export interface ExecutiveSignals {
    total_trade_spend_committed: number;
    total_trade_spend_flexible: number;
    incremental_revenue_ytd: number;
    incremental_revenue_last_cycle: number;
    net_margin_delivered: number;
    promotions_at_risk_count: number;
    promotions_at_risk_value: number;
    decisions_required_30: number;
    decisions_required_60: number;
    decisions_required_90: number;
    investment_reallocated: number;
    learning_impact_score: number;
  }
  
  // Tool Categories - Full list per spec
  export const toolCategories: ToolCategory[] = [
    {
      category: "Traditional",
      tools: [
        { id: "feature", name: "Feature", category: "Traditional", cost_multiplier: 1.2, lift_contribution: 0.22, elasticity_interaction: 1.15, historical_effectiveness: 0.78 },
        { id: "display", name: "Display", category: "Traditional", cost_multiplier: 1.15, lift_contribution: 0.18, elasticity_interaction: 1.12, historical_effectiveness: 0.75 },
        { id: "shelf_tags", name: "Shelf Tags", category: "Traditional", cost_multiplier: 0.5, lift_contribution: 0.08, elasticity_interaction: 1.05, historical_effectiveness: 0.65 },
        { id: "secondary_placement", name: "Secondary Placement", category: "Traditional", cost_multiplier: 1.3, lift_contribution: 0.24, elasticity_interaction: 1.18, historical_effectiveness: 0.72 },
        { id: "signage", name: "Signage", category: "Traditional", cost_multiplier: 0.6, lift_contribution: 0.10, elasticity_interaction: 1.06, historical_effectiveness: 0.68 },
        { id: "sampling", name: "Sampling", category: "Traditional", cost_multiplier: 1.8, lift_contribution: 0.28, elasticity_interaction: 1.22, historical_effectiveness: 0.82 },
        { id: "packaging", name: "Packaging", category: "Traditional", cost_multiplier: 0.8, lift_contribution: 0.12, elasticity_interaction: 1.08, historical_effectiveness: 0.70 },
      ],
    },
    {
      category: "In-Store Electronic",
      tools: [
        { id: "digital_screens", name: "Digital Screens (Endcaps/Aisles)", category: "In-Store Electronic", cost_multiplier: 1.4, lift_contribution: 0.20, elasticity_interaction: 1.14, historical_effectiveness: 0.74 },
        { id: "audio_announcements", name: "Audio Announcements", category: "In-Store Electronic", cost_multiplier: 0.4, lift_contribution: 0.06, elasticity_interaction: 1.04, historical_effectiveness: 0.55 },
        { id: "smart_carts", name: "Smart Carts / Kiosks", category: "In-Store Electronic", cost_multiplier: 1.6, lift_contribution: 0.22, elasticity_interaction: 1.16, historical_effectiveness: 0.68 },
        { id: "shelf_edge_displays", name: "Electronic Shelf Labels", category: "In-Store Electronic", cost_multiplier: 0.9, lift_contribution: 0.14, elasticity_interaction: 1.10, historical_effectiveness: 0.72 },
      ],
    },
    {
      category: "Retail Media - On-Site",
      tools: [
        { id: "sponsored_product", name: "Sponsored Product Listings", category: "Retail Media - On-Site", cost_multiplier: 1.25, lift_contribution: 0.18, elasticity_interaction: 1.12, historical_effectiveness: 0.76 },
        { id: "sponsored_brand", name: "Sponsored Brand Banners", category: "Retail Media - On-Site", cost_multiplier: 1.35, lift_contribution: 0.20, elasticity_interaction: 1.14, historical_effectiveness: 0.74 },
        { id: "homepage_takeover", name: "Homepage Takeovers", category: "Retail Media - On-Site", cost_multiplier: 2.0, lift_contribution: 0.32, elasticity_interaction: 1.24, historical_effectiveness: 0.80 },
        { id: "digital_coupons", name: "Digital Coupons / Offers", category: "Retail Media - On-Site", cost_multiplier: 0.7, lift_contribution: 0.12, elasticity_interaction: 1.08, historical_effectiveness: 0.72 },
        { id: "personalization", name: "Personalization Modules", category: "Retail Media - On-Site", cost_multiplier: 1.5, lift_contribution: 0.26, elasticity_interaction: 1.20, historical_effectiveness: 0.78 },
      ],
    },
    {
      category: "Retail Media - Off-Site",
      tools: [
        { id: "display_ads", name: "Display Ads (Programmatic)", category: "Retail Media - Off-Site", cost_multiplier: 1.1, lift_contribution: 0.14, elasticity_interaction: 1.10, historical_effectiveness: 0.70 },
        { id: "paid_social", name: "Paid Social (Meta, TikTok, Pinterest)", category: "Retail Media - Off-Site", cost_multiplier: 1.45, lift_contribution: 0.28, elasticity_interaction: 1.22, historical_effectiveness: 0.85 },
        { id: "online_video", name: "Online Video / CTV", category: "Retail Media - Off-Site", cost_multiplier: 1.7, lift_contribution: 0.26, elasticity_interaction: 1.18, historical_effectiveness: 0.77 },
        { id: "retargeting", name: "Prospecting & Retargeting", category: "Retail Media - Off-Site", cost_multiplier: 1.3, lift_contribution: 0.20, elasticity_interaction: 1.14, historical_effectiveness: 0.79 },
      ],
    },
    {
      category: "Location-Based",
      tools: [
        { id: "geo_fencing", name: "Geo-Fencing", category: "Location-Based", cost_multiplier: 1.55, lift_contribution: 0.24, elasticity_interaction: 1.16, historical_effectiveness: 0.73 },
      ],
    },
  ];
  
  // TRINITI Organics Company Info
  export const companyInfo = {
    name: "TRINITI Organics",
    tagline: "Premium Organic Frozen Foods",
    categories: ["Central American", "Asian", "Mediterranean"],
    totalSales2023: 26463967,
    totalSales2022: 20154056,
    totalSales2021: 14016056,
    yoyGrowth: 31.3,
    totalUnits2023: 15785532,
  };
  
  // DATASETS - TRINITI Organics Retailers
  export const retailers: Retailer[] = [
    {
      retailer_id: "R1",
      name: "Wegman’s Market",
      economic_model: "Hybrid",
      promo_cadence: "Seasonal + Event",
      lock_in_window_days: 120,
      pricing_lock_in_days: 90,
      tool_lock_in_days: 45,
      execution_reliability: 0.92,
      elasticity_sensitivity: "Medium",
      roi_index: 1.42,
      incrementality_ratio: 0.58,
      margin_volatility: 0.12,
      margin_structure: 32,
      reconciliation_likelihood: 0.15,
      ytd_sales: 23386400,
      ytd_units: 13937484,
      avg_merchandising_pct: 38.7,
    },
    {
      retailer_id: "R2",
      name: "Whole Foods Market",
      economic_model: "Pricing-Led",
      promo_cadence: "Weekly + Seasonal",
      lock_in_window_days: 90,
      pricing_lock_in_days: 60,
      tool_lock_in_days: 30,
      execution_reliability: 0.85,
      elasticity_sensitivity: "High",
      roi_index: 1.28,
      incrementality_ratio: 0.45,
      margin_volatility: 0.18,
      margin_structure: 28,
      reconciliation_likelihood: 0.08,
      ytd_sales: 3077567,
      ytd_units: 1848048,
      avg_merchandising_pct: 38.7,
    },
  ];
  
  // SKUs with unit measures
  export const skus: SKU[] = [
    { sku_id: "S1", brand: "TRINITI Organics", category: "Frozen Meals", variety: "Central American", base_price: 1.99, unit_measure: "12oz package" },
    { sku_id: "S2", brand: "TRINITI Organics", category: "Frozen Meals", variety: "Asian", base_price: 1.99, unit_measure: "12oz package" },
    { sku_id: "S3", brand: "TRINITI Organics", category: "Frozen Meals", variety: "Mediterranean", base_price: 1.99, unit_measure: "12oz package" },
  ];
  
  // Historical Promotions with enhanced data
  export const historicalPromotions: HistoricalPromotion[] = [
    {
      promotion_id: "P101",
      retailer_id: "R1",
      sku_id: "S1",
      discount_depth_pct: 15,
      discount_depth_cents: 30,
      tools: ["display", "paid_social"],
      trade_spend: 85000,
      incremental_units: 216600,
      incremental_revenue: 431119,
      net_margin: 125000,
      volume_split: { incremental_consumption: 0.52, pantry_loading: 0.28, forward_buying: 0.12, subsidized_base: 0.08 },
      timing_effects: { pre_promo_lift: 0.05, in_promo_lift: 2.8, post_promo_dip: -0.15 },
      status: "Completed",
      period: "Q1 2023",
      planned_margin: 140000,
      actual_vs_planned: -10.7,
    },
    {
      promotion_id: "P102",
      retailer_id: "R1",
      sku_id: "S2",
      discount_depth_pct: 12,
      discount_depth_cents: 24,
      tools: ["feature", "paid_social"],
      trade_spend: 72000,
      incremental_units: 145200,
      incremental_revenue: 288951,
      net_margin: 89000,
      volume_split: { incremental_consumption: 0.48, pantry_loading: 0.32, forward_buying: 0.14, subsidized_base: 0.06 },
      timing_effects: { pre_promo_lift: 0.08, in_promo_lift: 2.4, post_promo_dip: -0.18 },
      status: "Completed",
      period: "Q1 2023",
      planned_margin: 95000,
      actual_vs_planned: -6.3,
    },
    {
      promotion_id: "P103",
      retailer_id: "R1",
      sku_id: "S3",
      discount_depth_pct: 18,
      discount_depth_cents: 36,
      tools: ["display", "paid_social", "geo_fencing"],
      trade_spend: 94000,
      incremental_units: 295800,
      incremental_revenue: 588618,
      net_margin: 172000,
      volume_split: { incremental_consumption: 0.55, pantry_loading: 0.25, forward_buying: 0.12, subsidized_base: 0.08 },
      timing_effects: { pre_promo_lift: 0.12, in_promo_lift: 3.2, post_promo_dip: -0.08 },
      status: "Completed",
      period: "Q2 2023",
      planned_margin: 165000,
      actual_vs_planned: 4.2,
    },
    {
      promotion_id: "P104",
      retailer_id: "R2",
      sku_id: "S1",
      discount_depth_pct: 20,
      discount_depth_cents: 40,
      tools: ["display", "feature"],
      trade_spend: 45000,
      incremental_units: 89200,
      incremental_revenue: 177520,
      net_margin: 48000,
      volume_split: { incremental_consumption: 0.45, pantry_loading: 0.35, forward_buying: 0.15, subsidized_base: 0.05 },
      timing_effects: { pre_promo_lift: 0.02, in_promo_lift: 2.1, post_promo_dip: -0.22 },
      status: "Completed",
      period: "Q2 2023",
      planned_margin: 52000,
      actual_vs_planned: -7.7,
    },
    {
      promotion_id: "P105",
      retailer_id: "R2",
      sku_id: "S2",
      discount_depth_pct: 22,
      discount_depth_cents: 44,
      tools: ["feature", "online_video"],
      trade_spend: 38000,
      incremental_units: 62400,
      incremental_revenue: 124182,
      net_margin: 28000,
      volume_split: { incremental_consumption: 0.40, pantry_loading: 0.38, forward_buying: 0.16, subsidized_base: 0.06 },
      timing_effects: { pre_promo_lift: 0.01, in_promo_lift: 1.8, post_promo_dip: -0.28 },
      status: "Completed",
      period: "Q3 2023",
      planned_margin: 38000,
      actual_vs_planned: -26.3,
    },
    {
      promotion_id: "P106",
      retailer_id: "R2",
      sku_id: "S3",
      discount_depth_pct: 25,
      discount_depth_cents: 50,
      tools: ["display", "display_ads"],
      trade_spend: 52000,
      incremental_units: 98400,
      incremental_revenue: 195822,
      net_margin: 35000,
      volume_split: { incremental_consumption: 0.38, pantry_loading: 0.40, forward_buying: 0.18, subsidized_base: 0.04 },
      timing_effects: { pre_promo_lift: 0.03, in_promo_lift: 2.0, post_promo_dip: -0.25 },
      status: "Completed",
      period: "Q3 2023",
      planned_margin: 48000,
      actual_vs_planned: -27.1,
    },
    {
      promotion_id: "P107",
      retailer_id: "R1",
      sku_id: "S1",
      discount_depth_pct: 10,
      discount_depth_cents: 20,
      tools: [],
      trade_spend: 65000,
      incremental_units: 178500,
      incremental_revenue: 355163,
      net_margin: 145000,
      volume_split: { incremental_consumption: 0.58, pantry_loading: 0.22, forward_buying: 0.12, subsidized_base: 0.08 },
      timing_effects: { pre_promo_lift: 0.04, in_promo_lift: 2.2, post_promo_dip: -0.10 },
      status: "Completed",
      period: "Q4 2023",
      planned_margin: 138000,
      actual_vs_planned: 5.1,
    },
    {
      promotion_id: "P108",
      retailer_id: "R1",
      sku_id: "S3",
      discount_depth_pct: 15,
      discount_depth_cents: 30,
      tools: ["display", "paid_social", "geo_fencing"],
      trade_spend: 118000,
      incremental_units: 312000,
      incremental_revenue: 620880,
      net_margin: 195000,
      volume_split: { incremental_consumption: 0.52, pantry_loading: 0.28, forward_buying: 0.14, subsidized_base: 0.06 },
      timing_effects: { pre_promo_lift: 0.15, in_promo_lift: 3.5, post_promo_dip: -0.05 },
      status: "Completed",
      period: "Q4 2023",
      planned_margin: 180000,
      actual_vs_planned: 8.3,
    },
  ];
  
  // Planned Promotions for 2024
  export const plannedPromotions: PlannedPromotion[] = [
    {
      promotion_id: "P201",
      retailer_id: "R1",
      sku_id: "S1",
      planned_discount_pct: 12,
      planned_discount_cents: 24,
      tools: ["display", "paid_social", "geo_fencing"],
      planned_trade_spend: 95000,
      expected_incremental_revenue: 485000,
      expected_margin: 168000,
      risk_level: "Low",
      confidence: 0.82,
      status: "Planned",
      promo_window: "Mar 1 - Mar 14, 2024",
      auto_tag: "Scale",
      ownership: { sales: "M. Chen", rgm: "S. Patel", finance: "J. Williams" },
    },
    {
      promotion_id: "P202",
      retailer_id: "R1",
      sku_id: "S2",
      planned_discount_pct: 15,
      planned_discount_cents: 30,
      tools: ["feature", "online_video", "display_ads"],
      planned_trade_spend: 88000,
      expected_incremental_revenue: 420000,
      expected_margin: 142000,
      risk_level: "Low",
      confidence: 0.78,
      status: "Planned",
      promo_window: "Mar 15 - Mar 28, 2024",
      auto_tag: "Scale",
      ownership: { sales: "M. Chen", rgm: "S. Patel", finance: "J. Williams" },
    },
    {
      promotion_id: "P203",
      retailer_id: "R1",
      sku_id: "S3",
      planned_discount_pct: 18,
      planned_discount_cents: 36,
      tools: ["display", "paid_social", "homepage_takeover"],
      planned_trade_spend: 125000,
      expected_incremental_revenue: 680000,
      expected_margin: 215000,
      risk_level: "Medium",
      confidence: 0.72,
      status: "Planned",
      promo_window: "Apr 1 - Apr 21, 2024",
      auto_tag: "Review",
      ownership: { sales: "M. Chen", rgm: "S. Patel", finance: "J. Williams" },
    },
    {
      promotion_id: "P204",
      retailer_id: "R2",
      sku_id: "S1",
      planned_discount_pct: 20,
      planned_discount_cents: 40,
      tools: ["display", "feature"],
      planned_trade_spend: 58000,
      expected_incremental_revenue: 245000,
      expected_margin: 72000,
      risk_level: "Medium",
      confidence: 0.68,
      status: "Planned",
      promo_window: "Mar 8 - Mar 22, 2024",
      auto_tag: "Fix",
      ownership: { sales: "R. Garcia", rgm: "S. Patel", finance: "J. Williams" },
    },
    {
      promotion_id: "P205",
      retailer_id: "R2",
      sku_id: "S3",
      planned_discount_pct: 22,
      planned_discount_cents: 44,
      tools: ["display", "paid_social"],
      planned_trade_spend: 62000,
      expected_incremental_revenue: 275000,
      expected_margin: 78000,
      risk_level: "High",
      confidence: 0.58,
      status: "Planned",
      promo_window: "Apr 5 - Apr 19, 2024",
      auto_tag: "Kill",
      ownership: { sales: "R. Garcia", rgm: "S. Patel", finance: "J. Williams" },
    },
    {
      promotion_id: "P206",
      retailer_id: "R1",
      sku_id: "S2",
      planned_discount_pct: 10,
      planned_discount_cents: 20,
      tools: ["sampling", "digital_screens"],
      planned_trade_spend: 78000,
      expected_incremental_revenue: 380000,
      expected_margin: 135000,
      risk_level: "Low",
      confidence: 0.85,
      status: "Frozen",
      promo_window: "May 1 - May 14, 2024",
      auto_tag: "Scale",
      ownership: { sales: "M. Chen", rgm: "S. Patel", finance: "J. Williams" },
    },
  ];
  
  // Scenario rules with all tools
  export const scenarioRules: ScenarioRules = {
    elasticity_rules: { Low: 1.1, Medium: 1.25, High: 1.4 },
    tool_lift: {
      feature: 1.22,
      display: 1.18,
      shelf_tags: 1.08,
      secondary_placement: 1.24,
      signage: 1.10,
      sampling: 1.28,
      packaging: 1.12,
      digital_screens: 1.20,
      audio_announcements: 1.06,
      smart_carts: 1.22,
      shelf_edge_displays: 1.14,
      sponsored_product: 1.18,
      sponsored_brand: 1.20,
      homepage_takeover: 1.32,
      digital_coupons: 1.12,
      personalization: 1.26,
      display_ads: 1.14,
      paid_social: 1.28,
      online_video: 1.26,
      retargeting: 1.20,
      geo_fencing: 1.24,
    },
    margin_decay_threshold_pct: 18,
    margin_penalty_per_extra_pct: 0.04,
  };
  
  export const geoFencingMetrics: GeoFencingMetrics = {
    promotion_id: "P203",
    geo_fencing: {
      impressions: 2400000,
      store_visits: 168000,
      store_visit_conversion: 7.0,
      purchases: 62000,
      purchase_conversion: 36.9,
      incremental_units: 44000,
      cost_per_incremental_unit: 2.84,
    },
  };
  
  export const budgetGuardrails: BudgetGuardrails = {
    total_trade_budget: 1500000,
    committed_spend: 1125000,
    flexible_spend: 375000,
    allocations: [
      { retailer_id: "R1", allocated_pct: 0.70, fair_share_pct: 0.72 },
      { retailer_id: "R2", allocated_pct: 0.30, fair_share_pct: 0.28 },
    ],
    flexibility_pct: 0.25,
    regulatory_safe_window_days: 14,
  };
  
  export const decisionLocks: DecisionLock[] = [
    {
      promotion_id: "P201",
      pricing_lock_date: "2024-02-01",
      tool_lock_date: "2024-02-15",
      contracted_components: ["Pricing", "Display"],
      flexible_components: ["Paid Social", "Geo-Fencing"],
      locked: false,
    },
    {
      promotion_id: "P202",
      pricing_lock_date: "2024-02-15",
      tool_lock_date: "2024-03-01",
      contracted_components: ["Pricing", "Feature"],
      flexible_components: ["Online Video"],
      locked: false,
    },
    {
      promotion_id: "P206",
      pricing_lock_date: "2024-03-15",
      tool_lock_date: "2024-04-01",
      contracted_components: ["Pricing", "Sampling", "Digital Screens"],
      flexible_components: [],
      locked: true,
      locked_by: "M. Chen",
      locked_date: "2024-01-20",
    },
  ];
  
  export const learnings: Learning[] = [
    {
      promotion_id: "P101",
      planned_margin: 140000,
      actual_margin: 125000,
      planned_incrementality: 0.55,
      actual_incrementality: 0.52,
      root_cause: "Elasticity Miss",
      root_cause_detail: "Central American line showed lower price elasticity than modeled",
      learning: "Shift Q2 focus to Mediterranean; increase display support for Central American",
      confidence_update: -0.03,
      assumptions_validated: ["Social channels effective", "WF execution reliable"],
      assumptions_broken: ["Central American elasticity at 1.3x"],
    },
    {
      promotion_id: "P103",
      planned_margin: 165000,
      actual_margin: 172000,
      planned_incrementality: 0.50,
      actual_incrementality: 0.55,
      root_cause: "Retailer Behavior",
      root_cause_detail: "TikTok campaign viral moment drove unexpected traffic to WF stores",
      learning: "Increase TikTok allocation for Mediterranean line; prepare inventory buffers",
      confidence_update: 0.08,
      assumptions_validated: ["Mediterranean high elasticity", "Geo-fencing ROI positive"],
      assumptions_broken: [],
    },
    {
      promotion_id: "P105",
      planned_margin: 38000,
      actual_margin: 28000,
      planned_incrementality: 0.48,
      actual_incrementality: 0.40,
      root_cause: "Timing Distortion",
      root_cause_detail: "Whole Foods Market weekly cadence led to excessive pantry loading - 38% of volume",
      learning: "Limit discount depth at Whole Foods Market to 18%; favor feature over display",
      confidence_update: -0.06,
      assumptions_validated: [],
      assumptions_broken: ["Whole Foods Market can handle 22% depth", "YouTube effective at Whole Foods Market"],
    },
    {
      promotion_id: "P108",
      planned_margin: 180000,
      actual_margin: 195000,
      planned_incrementality: 0.48,
      actual_incrementality: 0.52,
      root_cause: "Execution Failure",
      root_cause_detail: "Despite WF display execution at 87%, social channels overdelivered",
      learning: "Bundle social channels for Mediterranean line; optimal discount at 15%",
      confidence_update: 0.10,
      assumptions_validated: ["Mediterranean premium elasticity", "Social + Geo synergy"],
      assumptions_broken: [],
    },
  ];
  
  export const baselineDiagnostics: BaselineDiagnostics[] = [
    {
      retailer_id: "R1",
      sku_id: "S1",
      baseline_curve: [100, 102, 98, 105, 110, 108, 115, 120, 118, 112, 105, 125],
      seasonality_overlay: [0.92, 0.95, 1.02, 1.08, 1.05, 0.98, 1.12, 1.15, 1.08, 0.95, 0.88, 1.18],
      promo_weeks: [3, 7, 11],
      non_promo_weeks: [1, 2, 4, 5, 6, 8, 9, 10, 12],
      confidence_score: 0.85,
      syndicated_estimate: 95,
    },
    {
      retailer_id: "R1",
      sku_id: "S3",
      baseline_curve: [120, 125, 118, 130, 135, 128, 142, 148, 140, 132, 125, 155],
      seasonality_overlay: [0.90, 0.93, 1.05, 1.12, 1.08, 0.95, 1.18, 1.22, 1.10, 0.92, 0.85, 1.25],
      promo_weeks: [4, 8, 12],
      non_promo_weeks: [1, 2, 3, 5, 6, 7, 9, 10, 11],
      confidence_score: 0.88,
      syndicated_estimate: 112,
    },
    {
      retailer_id: "R2",
      sku_id: "S1",
      baseline_curve: [45, 48, 42, 52, 55, 50, 58, 62, 55, 48, 42, 65],
      seasonality_overlay: [0.88, 0.92, 1.08, 1.15, 1.10, 0.95, 1.20, 1.25, 1.12, 0.90, 0.82, 1.28],
      promo_weeks: [2, 6, 10],
      non_promo_weeks: [1, 3, 4, 5, 7, 8, 9, 11, 12],
      confidence_score: 0.72,
      syndicated_estimate: 42,
    },
    {
      retailer_id: "R1",
      sku_id: "S2",
      baseline_curve: [110, 113, 108, 118, 122, 116, 128, 132, 125, 118, 112, 138],
      seasonality_overlay: [0.91, 0.94, 1.03, 1.10, 1.06, 0.97, 1.14, 1.17, 1.09, 0.94, 0.87, 1.20],
      promo_weeks: [3, 7, 11],
      non_promo_weeks: [1, 2, 4, 5, 6, 8, 9, 10, 12],
      confidence_score: 0.83,
      syndicated_estimate: 102,
    },
    {
      retailer_id: "R2",
      sku_id: "S2",
      baseline_curve: [38, 40, 36, 44, 47, 42, 50, 54, 48, 42, 36, 58],
      seasonality_overlay: [0.86, 0.91, 1.06, 1.13, 1.08, 0.94, 1.18, 1.23, 1.10, 0.89, 0.80, 1.26],
      promo_weeks: [4, 8, 12],
      non_promo_weeks: [1, 2, 3, 5, 6, 7, 9, 10, 11],
      confidence_score: 0.70,
      syndicated_estimate: 36,
    },
    {
      retailer_id: "R2",
      sku_id: "S3",
      baseline_curve: [52, 55, 49, 60, 64, 58, 68, 72, 64, 56, 50, 76],
      seasonality_overlay: [0.87, 0.90, 1.07, 1.14, 1.09, 0.94, 1.19, 1.24, 1.11, 0.89, 0.81, 1.27],
      promo_weeks: [3, 7, 11],
      non_promo_weeks: [1, 2, 4, 5, 6, 8, 9, 10, 12],
      confidence_score: 0.75,
      syndicated_estimate: 50,
    },
  ];
  
  export const monitoringAlerts: MonitoringAlert[] = [
    {
      promotion_id: "P201",
      alert_type: "Lift Variance",
      severity: "Medium",
      message: "Week 1 lift at 85% of expected - monitor closely",
      expected_value: 2.8,
      actual_value: 2.38,
      variance_pct: -15,
    },
    {
      promotion_id: "P204",
      alert_type: "Execution Issue",
      severity: "High",
      message: "Display compliance at 68% vs 90% target at Whole Foods Market",
      expected_value: 90,
      actual_value: 68,
      variance_pct: -24.4,
    },
    {
      promotion_id: "P205",
      alert_type: "Margin At Risk",
      severity: "High",
      message: "Current trajectory: -$18K margin shortfall",
      expected_value: 78000,
      actual_value: 60000,
      variance_pct: -23.1,
    },
  ];
  
  export const executiveSignals: ExecutiveSignals = {
    total_trade_spend_committed: 1125000,
    total_trade_spend_flexible: 375000,
    incremental_revenue_ytd: 2782452,
    incremental_revenue_last_cycle: 620880,
    net_margin_delivered: 837000,
    promotions_at_risk_count: 2,
    promotions_at_risk_value: 140000,
    decisions_required_30: 3,
    decisions_required_60: 5,
    decisions_required_90: 2,
    investment_reallocated: 85000,
    learning_impact_score: 0.12,
  };
  
  // Monthly Sales Data
  export const monthlySalesData = [
    { month: "Jan 2023", wegmans: 1750406, wholefoods: 234794, total: 1985200, marketing_spend: 104628, marketing_roi: 18.97 },
    { month: "Feb 2023", wegmans: 1840906, wholefoods: 239081, total: 2079988, marketing_spend: 109273, marketing_roi: 19.03 },
    { month: "Mar 2023", wegmans: 2113071, wholefoods: 298868, total: 2411940, marketing_spend: 131023, marketing_roi: 18.41 },
    { month: "Apr 2023", wegmans: 1878498, wholefoods: 238156, total: 2116654, marketing_spend: 91009, marketing_roi: 23.26 },
    { month: "May 2023", wegmans: 1835859, wholefoods: 228709, total: 2064567, marketing_spend: 130563, marketing_roi: 15.81 },
    { month: "Jun 2023", wegmans: 2250851, wholefoods: 295043, total: 2545894, marketing_spend: 104094, marketing_roi: 24.46 },
    { month: "Jul 2023", wegmans: 1858733, wholefoods: 242378, total: 2101111, marketing_spend: 113960, marketing_roi: 18.44 },
    { month: "Aug 2023", wegmans: 1936407, wholefoods: 234333, total: 2170740, marketing_spend: 106710, marketing_roi: 20.34 },
    { month: "Sep 2023", wegmans: 2295373, wholefoods: 294475, total: 2589848, marketing_spend: 117830, marketing_roi: 21.98 },
    { month: "Oct 2023", wegmans: 1878822, wholefoods: 240407, total: 2119229, marketing_spend: 114458, marketing_roi: 18.52 },
    { month: "Nov 2023", wegmans: 1570794, wholefoods: 234312, total: 1805107, marketing_spend: 71639, marketing_roi: 25.20 },
    { month: "Dec 2023", wegmans: 2176679, wholefoods: 297010, total: 2473690, marketing_spend: 140610, marketing_roi: 17.59 },
  ];
  
  // Helper functions
  export const getRetailerById = (id: string): Retailer | undefined => retailers.find((r) => r.retailer_id === id);
  export const getSkuById = (id: string): SKU | undefined => skus.find((s) => s.sku_id === id);
  export const getRetailerName = (id: string): string => getRetailerById(id)?.name || "Unknown";
  export const getSkuInfo = (id: string): string => getSkuById(id)?.variety || "Unknown";
  
  export const formatCurrency = (value: number): string =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
  
  export const formatPercent = (value: number): string => `${(value * 100).toFixed(0)}%`;
  export const formatNumber = (value: number): string => new Intl.NumberFormat("en-US").format(value);
  
  export const getTotalTradeSpend = (): number => {
    const historical = historicalPromotions.reduce((sum, p) => sum + p.trade_spend, 0);
    const planned = plannedPromotions.reduce((sum, p) => sum + p.planned_trade_spend, 0);
    return historical + planned;
  };
  
  export const getTotalMargin = (): number => {
    const historical = historicalPromotions.reduce((sum, p) => sum + p.net_margin, 0);
    const planned = plannedPromotions.reduce((sum, p) => sum + p.expected_margin, 0);
    return historical + planned;
  };
  
  export const getPromotionsAtRisk = (): PlannedPromotion[] =>
    plannedPromotions.filter((p) => p.risk_level === "High" || p.confidence < 0.6);
  
  export const calculateProjectedMetrics = (
    baseSpend: number,
    discountPct: number,
    tools: string[],
    elasticity: "Low" | "Medium" | "High"
  ) => {
    const elasticityMultiplier = scenarioRules.elasticity_rules[elasticity];
    const toolMultiplier = tools.reduce((mult, tool) => mult * (scenarioRules.tool_lift[tool] || 1), 1);
    const baseRevenue = baseSpend * 1.3;
    const projectedRevenue = baseRevenue * elasticityMultiplier * toolMultiplier;
    let marginMultiplier = 1;
    if (discountPct > scenarioRules.margin_decay_threshold_pct) {
      const extraPct = discountPct - scenarioRules.margin_decay_threshold_pct;
      marginMultiplier = 1 - extraPct * scenarioRules.margin_penalty_per_extra_pct;
    }
    const projectedMargin = (projectedRevenue - baseSpend) * marginMultiplier * 0.3;
    const roi = projectedRevenue / baseSpend;
    const riskScore = discountPct > 20 ? "High" : discountPct > 15 ? "Medium" : "Low";
    const confidence = Math.max(0.5, 0.95 - discountPct * 0.015 - tools.length * 0.02);
    return {
      projectedRevenue: Math.round(projectedRevenue),
      projectedMargin: Math.round(projectedMargin),
      roi: Math.round(roi * 100) / 100,
      riskScore,
      confidence: Math.round(confidence * 100) / 100,
      incrementalUnits: Math.round(projectedRevenue / 1.99),
    };
  };
  
  export const getYoYGrowth = (): number => ((companyInfo.totalSales2023 - companyInfo.totalSales2022) / companyInfo.totalSales2022) * 100;
  export const getAverageROI = (): number => {
    const allRevenues = [
      ...historicalPromotions.map((p) => p.incremental_revenue),
      ...plannedPromotions.map((p) => p.expected_incremental_revenue),
    ];
    const allSpends = [
      ...historicalPromotions.map((p) => p.trade_spend),
      ...plannedPromotions.map((p) => p.planned_trade_spend),
    ];
    const totalRevenue = allRevenues.reduce((sum, r) => sum + r, 0);
    const totalSpend = allSpends.reduce((sum, s) => sum + s, 0);
    return totalSpend > 0 ? totalRevenue / totalSpend : 0;
  };
  
  export const getRetailerContribution = (retailerId: string): number => {
    const retailer = getRetailerById(retailerId);
    if (!retailer?.ytd_sales) return 0;
    return (retailer.ytd_sales / companyInfo.totalSales2023) * 100;
  };
  